import React, { Fragment, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Clickable, Expandable, SearchInput, Stack } from '@moda/om';
import { sort, sortBy, Ord } from 'ramda';
import { compact } from '@moda/portal-stanchions';
import { usePLPParams, PLPParamKey } from '../../hooks/usePLPParams';
import { PageFilterFacet, Facet, FacetControlType } from './PageFilterFacet';
import { PageFilterPanel } from './PageFilterPanel';

import './PageFilterFacets.scss';

type PageFilterFacetsProps = {
  expandable?: boolean;
  virtual?: boolean;
  facetName: PLPParamKey;
  facets: (Facet | null)[] | null;
  mobile?: boolean;
  showCounts?: boolean;
  onClose: () => void;
  searchable?: boolean;
  sortingBy?: (facet: Facet) => Ord;
  title?: string | null;
  type?: FacetControlType;
};

const sortFacets = (facets: PageFilterFacetsProps['facets'], sortingBy?: (facet: Facet) => Ord) => {
  let sortedFacets = compact(facets) || [];
  if (facets && facets[0] && facets[0]?.category && facets[0]?.size) {
    // Sort sizes by numeric size and then by category

    sortedFacets = sort((a, b) => {
      if (!a?.size || !b?.size) {
        return 0;
      }

      const aSize = +a.size.split(' ')[1];
      const bSize = +b.size.split(' ')[1];

      return aSize - bSize;
    }, sortedFacets);

    sortedFacets = sortBy(facets => facets?.category || 0, sortedFacets);
  }

  if (facets && sortingBy) {
    sortedFacets = sortBy(sortingBy, sortedFacets);
  }

  return sortedFacets;
};

const getFacets = (
  facets: PageFilterFacetsProps['facets'],
  searchText: string | undefined,
  sortingBy?: (facet: Facet) => Ord
) => {
  const sortedFacets = sortFacets(facets, sortingBy);

  return searchText
    ? sortedFacets?.filter(facet => facet?.name?.toLowerCase().includes(searchText.toLowerCase()))
    : sortedFacets;
};

export const PageFilterFacets: React.FC<PageFilterFacetsProps> = ({
  expandable = false,
  virtual = false,
  facetName,
  facets,
  mobile = false,
  showCounts,
  onClose,
  searchable = false,
  title,
  type = 'checkbox',
  sortingBy = facet => facet.name as string
}) => {
  const { clearFilter } = usePLPParams();
  const [searchText, setSearchText] = useState('');
  const filteredFacets = useMemo(
    () => getFacets(facets, searchText, sortingBy),
    [facets, searchText, sortingBy]
  );
  const handleSearchClear = useCallback(() => setSearchText(''), [setSearchText]);

  if (!facets?.length) {
    return null;
  }

  let lastFacetCategory = '';
  const content = (
    <Stack space={2} className="PageFilterFacets">
      {!expandable && !mobile && <h4 className="PageFilterFacets__title">{title}</h4>}
      {searchable && (
        <SearchInput
          aria-label={`Search ${facetName}`}
          className="PageFilterFacets__search"
          onChange={setSearchText}
          onClear={handleSearchClear}
          placeholder="Search"
          value={searchText}
        />
      )}
      <div
        className={classNames('PageFilterFacets__facets', {
          'PageFilterFacets__facets--button': type === 'button'
        })}
      >
        {filteredFacets?.map(facet => {
          if (!facet) {
            return null;
          }

          const filterKey = facet.category
            ? facet.category + (facet.name || facet.size)
            : facet.name || facet.size || '';
          const showHeader = facet.category && lastFacetCategory !== facet.category;

          if (facet.category) {
            lastFacetCategory = facet.category;
          }

          return (
            <Fragment key={filterKey}>
              {showHeader && (
                <span className="PageFilterFacets__facetCategory">
                  {facet.category?.toUpperCase()}
                </span>
              )}
              <PageFilterFacet facet={facet} name={facetName} type={type} showCounts={showCounts} />
            </Fragment>
          );
        })}
      </div>
      {expandable && !mobile && (
        <Clickable
          className="PageFilterFacets__clear"
          onClick={() => clearFilter(facetName)}
          aria-label={`Clear ${facetName}`}
        >
          Clear
        </Clickable>
      )}
    </Stack>
  );

  if (mobile) {
    return (
      <PageFilterPanel onClose={onClose} title={title || ''}>
        {content}
      </PageFilterPanel>
    );
  }

  return expandable ? (
    <Expandable
      className="PageFilterFacets__expandable"
      icon="chevron"
      name={title || ''}
      virtual={virtual}
    >
      {content}
    </Expandable>
  ) : (
    <div className="PageFilterFacets__static">{content}</div>
  );
};
